import { Component, computed, effect, Inject, model, OnInit, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CrmFieldService, StandardIds } from '../../../../../shared-services/crm-services/field.service';

import { CrmDependencies, CrmInjectionToken } from '../../../../../tokens-and-interfaces';
import { MatCardModule } from '@angular/material/card';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';

import { ActivityInterface } from '@vendasta/crm';

import { SalespersonService, SalesSdk } from '@vendasta/sales';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { combineLatest, firstValueFrom, of, take } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SalesOpportunitiesSdk, SalesOpportunitiesService } from '@vendasta/sales-opportunities';
import { CrmPlatformApiService } from '../../../../../shared-services/platform-api.service';
import { ActivityCardComponent } from './activity-card/activity-card.component';
import { SanitizeHtmlPipe } from '../../../activities/sanitize-html.pipe';
import { ActivityAssociationService, ActivityIcons, ActivityTypes } from '../../../activities';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'crm-activity-opportunity-updated',
  templateUrl: './opportunity-change.component.html',
  styleUrls: ['./opportunity-change.component.scss'],
  providers: [CrmPlatformApiService],
  imports: [
    CommonModule,
    TranslateModule,
    MatCardModule,
    GalaxyPipesModule,
    GalaxyAvatarModule,
    SanitizeHtmlPipe,
    ActivityCardComponent,
    RouterLink,
  ],
})
export class CrmActivityOpportunityUpdatedComponent implements OnInit {
  activity = model<ActivityInterface>({});
  showPrimaryAssociation = model<boolean>(false);

  private readonly namespace = toSignal(this.config.namespace$);

  isClosedWon = signal<boolean>(false);
  salespersonId: Signal<string> = computed(() => {
    return this.getStringFieldData(this.activity(), StandardIds.ActivityOpportunityChangeSalespersonID);
  });

  primaryAssociation = this.associationService.primaryAssociation;
  routePrefix = this.associationService.routePrefix;

  effectResult = signal<EffectResult | undefined>(undefined);

  headerText = computed(() => {
    return this.translateService.instant('COMPANY.OPPORTUNITY_CHANGE_ACTIVITY.OPPORTUNITY_UPDATED');
  });

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    const headerText = this.headerText();
    const opportunityName = this.opportunityName();
    const updatedBy = this.effectResult()?.opportunityUpdatedBy;

    const opportunityChangeStatus = this.getStringFieldData(
      this.activity(),
      StandardIds.ActivityOpportunityChangePipelineStage,
    );
    const opportunityChangeLostReason = this.getStringFieldData(
      this.activity(),
      StandardIds.ActivityOpportunityChangeLostReason,
    );
    const opportunityChangeNotes = this.getStringFieldData(this.activity(), StandardIds.ActivityOpportunityChangeNotes);

    const formattedStatus =
      opportunityChangeStatus.charAt(0).toUpperCase() + opportunityChangeStatus.slice(1).toLowerCase();
    const formattedLostReason =
      opportunityChangeLostReason.charAt(0).toUpperCase() + opportunityChangeLostReason.slice(1).toLowerCase();

    const activityInfo = {
      status: formattedStatus,
      lostReason: formattedLostReason,
      notes: opportunityChangeNotes,
      opportunityName: opportunityName,
    } as OpportunityInfo;

    return {
      headerText,
      updatedBy,
      activityInfo,
    };
  });

  accountGroupId$ = toObservable(this.activity).pipe(
    map(
      (activity) =>
        (activity.associations || []).find((association) => association.crmObjectType === 'Company')?.crmObjectId ?? '',
    ),
    filter((companyId) => !!companyId),
    switchMap((companyId) => this.crmPlatformApiService.getAccountGroupForCompany(companyId)),
  );

  opportunityName = toSignal(
    combineLatest([this.accountGroupId$, toObservable(this.activity)]).pipe(
      switchMap(([accountGroupId, activity]) => {
        const opportunityId = this.getStringFieldData(activity, StandardIds.ActivityOpportunityChangeID);
        return this.opportunityApi.get(opportunityId, accountGroupId).pipe(
          map((opportunity) => opportunity.name),
          catchError(() => of('Unknown')),
        );
      }),
    ),
  );

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly translateService: TranslateService,
    private readonly crmPlatformApiService: CrmPlatformApiService,
    private readonly associationService: ActivityAssociationService,
    @Inject(SalespersonService) private readonly salespersonService: SalesSdk,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    @Inject(SalesOpportunitiesService) private readonly opportunityApi: SalesOpportunitiesSdk,
  ) {
    effect(() => {
      this.associationService.activity = this.activity();
      const namespace = this.namespace() || '';
      if (this.salespersonId()) {
        return this.salespersonService
          .getSalespersonBySubjectId(namespace, this.salespersonId())
          .pipe(
            map((salesperson) => `${salesperson.firstName} ${salesperson.lastName}`),
            catchError(() => of('Unknown')),
          )
          .pipe(take(1))
          .subscribe((salesperson) => {
            const effectResult = {} as EffectResult;
            effectResult.opportunityUpdatedBy = salesperson;
            this.effectResult.set(effectResult);
          });
      }
      return;
    });
  }

  ngOnInit(): void {
    this.checkClosedWon();
  }

  private checkClosedWon(): void {
    const opportunityChangeStatus = this.getStringFieldData(
      this.activity(),
      StandardIds.ActivityOpportunityChangePipelineStage,
    );
    this.isClosedWon.set(opportunityChangeStatus.toLowerCase() === 'closed-won');
  }

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  protected async handleOpenOpportunity(): Promise<void> {
    const opportunityId = this.getStringFieldData(this.activity(), StandardIds.ActivityOpportunityChangeID);
    const accountGroupId = await firstValueFrom(this.accountGroupId$);
    if (this.config.customActions && this.config.customActions.openOpportunitiesDetails) {
      this.config.customActions.openOpportunitiesDetails(opportunityId, accountGroupId);
    }
  }

  protected readonly ActivityIcons = ActivityIcons;
  protected readonly ActivityTypes = ActivityTypes;
}

interface ActivityCardInformation {
  headerText: string;
  updatedBy?: string;
  activityInfo: OpportunityInfo;
}

interface OpportunityInfo {
  status: string;
  lostReason: string;
  notes?: string;
  opportunityName?: string;
}

interface EffectResult {
  opportunityUpdatedBy: string;
}
